import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { 
    Plus,
    Pencil,
    Trash2,
    MoreHorizontal,
    CreditCard,
    Server,
    Building,
    Building2,
    Briefcase,
    Megaphone,
    Users,
    Scale,
    Receipt,
    Search,
    Filter,
    Loader2,
    Calendar,
    ArrowUpDown,
    Wallet,
    RefreshCw
} from 'lucide-react';

const API = `${API_BASE}/api`;

// Category icons mapping
const CATEGORY_ICONS = {
    subscriptions: CreditCard,
    hosting: Server,
    banking: Building,
    one_off: Receipt,
    professional: Briefcase,
    office: Building2,
    marketing: Megaphone,
    salaries: Users,
    legal: Scale,
    miscellaneous: MoreHorizontal,
};

// Category badge colors
const CATEGORY_BADGE_COLORS = {
    subscriptions: 'bg-blue-100 text-blue-700',
    hosting: 'bg-purple-100 text-purple-700',
    banking: 'bg-emerald-100 text-emerald-700',
    one_off: 'bg-amber-100 text-amber-700',
    professional: 'bg-indigo-100 text-indigo-700',
    office: 'bg-slate-100 text-slate-700',
    marketing: 'bg-pink-100 text-pink-700',
    salaries: 'bg-cyan-100 text-cyan-700',
    legal: 'bg-orange-100 text-orange-700',
    miscellaneous: 'bg-gray-100 text-gray-700',
};

// Recurrence labels
const RECURRENCE_LABELS = {
    one_time: 'One-time',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
};

// Period options
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

export default function OperatingExpensesPage() {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [summary, setSummary] = useState(null);
    
    // Period selection
    const [periodType, setPeriodType] = useState('month');
    const [year, setYear] = useState(new Date().getFullYear());
    const [periodValue, setPeriodValue] = useState(new Date().getMonth() + 1);
    
    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [vendorFilter, setVendorFilter] = useState('all');
    
    // Dialog state
    const [showDialog, setShowDialog] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    
    // Form state
    const [form, setForm] = useState({
        name: '',
        description: '',
        category: 'subscriptions',
        amount: '',
        currency: 'EUR',
        expense_date: new Date().toISOString().split('T')[0],
        recurrence: 'one_time',
        vendor: '',
        reference: '',
        notes: ''
    });

    // Get available years
    const availableYears = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return [currentYear, currentYear - 1, currentYear - 2];
    }, []);

    // Get unique vendors for filter
    const vendors = useMemo(() => {
        const vendorSet = new Set(expenses.map(e => e.vendor).filter(Boolean));
        return Array.from(vendorSet).sort();
    }, [expenses]);

    // Filtered expenses
    const filteredExpenses = useMemo(() => {
        return expenses.filter(exp => {
            // Search filter
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const matchesSearch = 
                    exp.name.toLowerCase().includes(query) ||
                    (exp.description || '').toLowerCase().includes(query) ||
                    (exp.vendor || '').toLowerCase().includes(query) ||
                    (exp.reference || '').toLowerCase().includes(query);
                if (!matchesSearch) return false;
            }
            
            // Category filter
            if (categoryFilter !== 'all' && exp.category !== categoryFilter) {
                return false;
            }
            
            // Vendor filter
            if (vendorFilter !== 'all' && exp.vendor !== vendorFilter) {
                return false;
            }
            
            return true;
        });
    }, [expenses, searchQuery, categoryFilter, vendorFilter]);

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchData();
    }, [periodType, year, periodValue]);

    const fetchCategories = async () => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${API}/expenses/categories`, { headers });
            setCategories(response.data.categories);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const params = { period_type: periodType, year, period_value: periodValue };
            
            const [expensesRes, summaryRes] = await Promise.all([
                axios.get(`${API}/expenses`, { headers, params }),
                axios.get(`${API}/expenses/summary/period`, { headers, params })
            ]);
            
            setExpenses(expensesRes.data);
            setSummary(summaryRes.data);
        } catch (error) {
            console.error('Failed to fetch expenses:', error);
            toast.error('Failed to load expenses');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!form.name || !form.amount) {
            toast.error('Name and amount are required');
            return;
        }

        setIsSaving(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const payload = {
                ...form,
                amount: parseFloat(form.amount)
            };

            if (editingExpense) {
                await axios.patch(`${API}/expenses/${editingExpense.id}`, payload, { headers });
                toast.success('Expense updated');
            } else {
                await axios.post(`${API}/expenses`, payload, { headers });
                toast.success('Expense created');
            }

            closeDialog();
            fetchData();
        } catch (error) {
            console.error('Failed to save expense:', error);
            toast.error('Failed to save expense');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            await axios.delete(`${API}/expenses/${id}`, { headers });
            toast.success('Expense deleted');
            setDeleteConfirmId(null);
            fetchData();
        } catch (error) {
            console.error('Failed to delete expense:', error);
            toast.error('Failed to delete expense');
        }
    };

    const openDialog = (expense = null) => {
        if (expense) {
            setEditingExpense(expense);
            setForm({
                name: expense.name,
                description: expense.description || '',
                category: expense.category,
                amount: expense.amount.toString(),
                currency: expense.currency,
                expense_date: expense.expense_date,
                recurrence: expense.recurrence,
                vendor: expense.vendor || '',
                reference: expense.reference || '',
                notes: expense.notes || ''
            });
        } else {
            setEditingExpense(null);
            setForm({
                name: '',
                description: '',
                category: 'subscriptions',
                amount: '',
                currency: 'EUR',
                expense_date: new Date().toISOString().split('T')[0],
                recurrence: 'one_time',
                vendor: '',
                reference: '',
                notes: ''
            });
        }
        setShowDialog(true);
    };

    const closeDialog = () => {
        setShowDialog(false);
        setEditingExpense(null);
    };

    const getPeriodLabel = () => {
        if (periodType === 'month') {
            return `${MONTHS[periodValue - 1]} ${year}`;
        } else if (periodType === 'quarter') {
            return `${QUARTERS[periodValue - 1]} ${year}`;
        } else if (periodType === 'ytd') {
            return `YTD ${year}`;
        } else {
            return `Full Year ${year}`;
        }
    };

    const getCategoryLabel = (categoryId) => {
        const cat = categories.find(c => c.id === categoryId);
        return cat?.label || categoryId;
    };

    return (
        <div className="min-h-screen bg-background" data-testid="operating-expenses-page">
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                            Operating Expenses
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            Manage company operating costs and overhead
                        </p>
                    </div>
                    
                    <Button onClick={() => openDialog()} data-testid="add-expense-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Expense
                    </Button>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card className="border border-slate-200">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-100 rounded-lg">
                                    <Wallet className="h-5 w-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {summary?.total_eur?.toLocaleString('da-DK') || 0} EUR
                                    </p>
                                    <p className="text-xs text-slate-500">Total for {getPeriodLabel()}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="border border-slate-200">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-slate-100 rounded-lg">
                                    <Receipt className="h-5 w-5 text-slate-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-slate-900">{summary?.expense_count || 0}</p>
                                    <p className="text-xs text-slate-500">Total Expenses</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    {/* Period Selector Cards */}
                    <Card className="border border-slate-200 md:col-span-2">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Calendar className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="flex items-center gap-2 flex-1">
                                    <Select value={periodType} onValueChange={(v) => {
                                        setPeriodType(v);
                                        if (v === 'month') setPeriodValue(new Date().getMonth() + 1);
                                        else if (v === 'quarter') setPeriodValue(Math.floor(new Date().getMonth() / 3) + 1);
                                        else setPeriodValue(1);
                                    }}>
                                        <SelectTrigger className="w-36 h-9" data-testid="period-type-selector">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="month">Month</SelectItem>
                                            <SelectItem value="quarter">Quarter</SelectItem>
                                            <SelectItem value="ytd">Year to Date</SelectItem>
                                            <SelectItem value="year">Full Year</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    {periodType === 'month' && (
                                        <Select value={periodValue.toString()} onValueChange={(v) => setPeriodValue(parseInt(v))}>
                                            <SelectTrigger className="w-32 h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {MONTHS.map((month, idx) => (
                                                    <SelectItem key={idx + 1} value={(idx + 1).toString()}>{month}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}

                                    {periodType === 'quarter' && (
                                        <Select value={periodValue.toString()} onValueChange={(v) => setPeriodValue(parseInt(v))}>
                                            <SelectTrigger className="w-24 h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {QUARTERS.map((q, idx) => (
                                                    <SelectItem key={idx + 1} value={(idx + 1).toString()}>{q}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}

                                    <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                                        <SelectTrigger className="w-24 h-9">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableYears.map((y) => (
                                                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <Card className="border border-slate-200 mb-6">
                    <CardContent className="pt-4">
                        <div className="flex flex-wrap items-center gap-4">
                            {/* Search */}
                            <div className="relative flex-1 min-w-[200px] max-w-sm">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    placeholder="Search expenses..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                    data-testid="search-expenses"
                                />
                            </div>

                            {/* Category Filter */}
                            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                <SelectTrigger className="w-48" data-testid="category-filter">
                                    <Filter className="h-4 w-4 mr-2 text-slate-400" />
                                    <SelectValue placeholder="Category" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Categories</SelectItem>
                                    {categories.map((cat) => (
                                        <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            {/* Vendor Filter */}
                            {vendors.length > 0 && (
                                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                                    <SelectTrigger className="w-40" data-testid="vendor-filter">
                                        <SelectValue placeholder="Vendor" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Vendors</SelectItem>
                                        {vendors.map((vendor) => (
                                            <SelectItem key={vendor} value={vendor}>{vendor}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}

                            {/* Clear Filters */}
                            {(searchQuery || categoryFilter !== 'all' || vendorFilter !== 'all') && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setCategoryFilter('all');
                                        setVendorFilter('all');
                                    }}
                                    className="text-slate-500"
                                >
                                    Clear filters
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Expenses Table */}
                <Card className="border border-slate-200">
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <LoadingSpinner />
                            </div>
                        ) : filteredExpenses.length === 0 ? (
                            <div className="text-center py-16">
                                <Wallet className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-slate-900 mb-1">
                                    {expenses.length === 0 ? 'No expenses yet' : 'No matching expenses'}
                                </h3>
                                <p className="text-sm text-slate-500 mb-4">
                                    {expenses.length === 0 
                                        ? `Start tracking your operating costs for ${getPeriodLabel()}`
                                        : 'Try adjusting your filters'
                                    }
                                </p>
                                {expenses.length === 0 && (
                                    <Button onClick={() => openDialog()}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add First Expense
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead className="w-10"></TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Vendor</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Frequency</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                        <TableHead className="w-10"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredExpenses.map((expense) => {
                                        const CategoryIcon = CATEGORY_ICONS[expense.category] || MoreHorizontal;
                                        const isCarryOver = expense.is_recurring_instance === true;
                                        return (
                                            <TableRow key={expense.id + (isCarryOver ? '-recurring' : '')} className={isCarryOver ? 'bg-slate-50/60' : ''} data-testid={`expense-row-${expense.id}`}>
                                                <TableCell>
                                                    <div className={`p-1.5 rounded ${CATEGORY_BADGE_COLORS[expense.category]?.split(' ')[0] || 'bg-slate-100'}`}>
                                                        <CategoryIcon className="h-4 w-4" />
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{expense.name}</p>
                                                        {isCarryOver && (
                                                            <p className="text-xs text-blue-500 flex items-center gap-1 mt-0.5">
                                                                <RefreshCw className="h-3 w-3" />
                                                                Recurring from {expense.expense_date}
                                                            </p>
                                                        )}
                                                        {!isCarryOver && expense.description && (
                                                            <p className="text-xs text-slate-500 truncate max-w-xs">{expense.description}</p>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="secondary" className={CATEGORY_BADGE_COLORS[expense.category]}>
                                                        {getCategoryLabel(expense.category)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-slate-600">
                                                    {expense.vendor || <span className="text-slate-400">—</span>}
                                                </TableCell>
                                                <TableCell className="text-slate-600">
                                                    {isCarryOver ? <span className="text-blue-500 italic">Recurring</span> : expense.expense_date}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`text-xs ${isCarryOver ? 'border-blue-300 text-blue-600 bg-blue-50' : ''}`}>
                                                        {RECURRENCE_LABELS[expense.recurrence]}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right font-semibold text-slate-900">
                                                    {expense.amount.toLocaleString('da-DK')} {expense.currency}
                                                </TableCell>
                                                <TableCell>
                                                    {!isCarryOver && (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => openDialog(expense)}>
                                                                    <Pencil className="h-4 w-4 mr-2" />
                                                                    Edit
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem 
                                                                    onClick={() => setDeleteConfirmId(expense.id)}
                                                                    className="text-red-600"
                                                                >
                                                                    <Trash2 className="h-4 w-4 mr-2" />
                                                                    Delete
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Results count */}
                {!loading && filteredExpenses.length > 0 && (
                    <p className="text-sm text-slate-500 mt-3">
                        Showing {filteredExpenses.length} of {expenses.length} expenses
                    </p>
                )}
            </main>

            {/* Add/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingExpense ? 'Edit Expense' : 'Add Expense'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label htmlFor="expense-name">Name *</Label>
                                <Input
                                    id="expense-name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="e.g., AWS Hosting"
                                    data-testid="expense-name-input"
                                />
                            </div>

                            <div>
                                <Label htmlFor="expense-category">Category</Label>
                                <Select
                                    value={form.category}
                                    onValueChange={(v) => setForm({ ...form, category: v })}
                                >
                                    <SelectTrigger id="expense-category" data-testid="expense-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map((cat) => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="expense-recurrence">Frequency</Label>
                                <Select
                                    value={form.recurrence}
                                    onValueChange={(v) => setForm({ ...form, recurrence: v })}
                                >
                                    <SelectTrigger id="expense-recurrence" data-testid="expense-recurrence-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="one_time">One-time</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                        <SelectItem value="quarterly">Quarterly</SelectItem>
                                        <SelectItem value="yearly">Yearly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="expense-amount">Amount *</Label>
                                <Input
                                    id="expense-amount"
                                    type="number"
                                    step="0.01"
                                    value={form.amount}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    placeholder="150.00"
                                    data-testid="expense-amount-input"
                                />
                            </div>

                            <div>
                                <Label htmlFor="expense-currency">Currency</Label>
                                <Select
                                    value={form.currency}
                                    onValueChange={(v) => setForm({ ...form, currency: v })}
                                >
                                    <SelectTrigger id="expense-currency" data-testid="expense-currency-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="EUR">EUR</SelectItem>
                                        <SelectItem value="DKK">DKK</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                        <SelectItem value="GBP">GBP</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="expense-date">Date</Label>
                                <Input
                                    id="expense-date"
                                    type="date"
                                    value={form.expense_date}
                                    onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                                    data-testid="expense-date-input"
                                />
                            </div>

                            <div>
                                <Label htmlFor="expense-vendor">Vendor</Label>
                                <Input
                                    id="expense-vendor"
                                    value={form.vendor}
                                    onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                                    placeholder="e.g., Amazon"
                                    data-testid="expense-vendor-input"
                                />
                            </div>

                            <div className="col-span-2">
                                <Label htmlFor="expense-notes">Notes</Label>
                                <Textarea
                                    id="expense-notes"
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    placeholder="Additional notes..."
                                    rows={2}
                                    data-testid="expense-notes-input"
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isSaving || !form.name || !form.amount}
                            data-testid="save-expense-btn"
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : editingExpense ? 'Update' : 'Add Expense'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Expense</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-600 py-4">
                        Are you sure you want to delete this expense? This action cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => handleDelete(deleteConfirmId)}
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
